import {
  canReadProperties,
  canWriteProperties,
  coreQueryKey,
  type CurrentAuthorization,
} from '@/api/core/authorization';
import {
  bindInitialRoomType,
  parseInitialTypeBindingReceipt,
  validateInitialTypeBindingCommand,
  type InitialRoomTypeBindingCommand,
  type InitialRoomTypeBindingReceipt,
  type InitialTypeBindingExpected,
} from '@/api/core/initial-type-binding';
import type { CatalogRoom, PropertyCatalog, PropertyCatalogCursors } from '@/api/core/properties';
import { readPropertyCatalog } from '@/api/core/properties';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mgrvtechgroup/admin-ds';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { freezeRequest } from '../presentation/command-owner';
import type { PropertyRuntime } from '../property-queries';

export interface BindingIntent {
  actorId?: string;
  namespace?: string;
  generation?: number;
  command: InitialRoomTypeBindingCommand;
  room: CatalogRoom;
  typeName: string;
  snapshotKey: string;
  originCursors: PropertyCatalogCursors;
  propertyName: string;
}

const originalBindings = new WeakMap<
  BindingIntent,
  {
    command: InitialRoomTypeBindingCommand;
    json: string;
    actor: string;
    namespace: string;
    generation: number;
    propertyId: string;
    roomId: string;
    client: PropertyRuntime['client'];
  }
>();

export interface InitialRoomTypeBindingOwnerState {
  intent: BindingIntent | null;
  unknown: boolean;
  message: string | null;
  receipt: InitialRoomTypeBindingReceipt | null;
  freshState: string | null;
  freshDetails: string | null;
  receiptContext: {
    commandId: string;
    roomId: string;
    roomNo: number;
    typeName: string;
    propertyName: string;
    cursors: PropertyCatalogCursors;
  } | null;
  pendingRequestId: string | null;
  pendingKind: 'review' | 'write' | 'replay' | null;
}

export const emptyInitialRoomTypeBindingOwnerState: InitialRoomTypeBindingOwnerState = {
  intent: null,
  unknown: false,
  message: null,
  receipt: null,
  freshState: null,
  freshDetails: null,
  receiptContext: null,
  pendingRequestId: null,
  pendingKind: null,
};

interface Props {
  page: PropertyCatalog;
  cursors: PropertyCatalogCursors;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  authorization: CurrentAuthorization | null;
  isPropertyCurrent: () => boolean;
  ownerState?: InitialRoomTypeBindingOwnerState;
  setOwnerState?: Dispatch<SetStateAction<InitialRoomTypeBindingOwnerState>>;
  ownerEpoch?: number;
  claimOwnerRequest?: (
    epoch: number,
    requestId: string,
    kind: 'review' | 'write' | 'replay',
  ) => boolean;
  isOwnerRequestCurrent?: (epoch: number, requestId: string) => boolean;
  getOwnerRequestSignal?: (epoch: number, requestId: string) => AbortSignal | undefined;
  finishOwnerRequest?: (epoch: number, requestId: string) => void;
  catalogError?: boolean;
}

function expectedFor(
  page: PropertyCatalog,
  propertyId: string,
  roomId: string,
  typeId: string,
): { room: CatalogRoom; typeName: string; expected: InitialTypeBindingExpected } | null {
  if (page.property.property_id.toLowerCase() !== propertyId.toLowerCase()) return null;
  const room = page.rooms.find((item) => item.id.toLowerCase() === roomId.toLowerCase());
  const type = page.roomTypes.find((item) => item.id.toLowerCase() === typeId.toLowerCase());
  if (
    !room ||
    !type ||
    room.coreRoomTypeId !== null ||
    room.version === null ||
    room.topologyRevision === null ||
    page.property.version < 1 ||
    type.version === null
  )
    return null;
  return {
    room,
    typeName: type.name,
    expected: {
      room_type_id: null,
      room_version: room.version,
      room_topology_revision: room.topologyRevision,
      property_version: page.property.version,
      type_version: type.version,
    },
  };
}

const snapshotKey = (expected: InitialTypeBindingExpected) => JSON.stringify(expected);

function failureMessage(error: unknown): string {
  if (!isApiError(error))
    return '요청 결과를 확인하지 못했습니다. 현재 객실 정보를 다시 조회해 주세요.';
  if (error.status === 403) return '현재 계정에 객실 유형 연결 권한이 없습니다.';
  if (error.status === 404) return '객실 또는 유형을 현재 지점에서 찾을 수 없습니다.';
  if (error.status === 422) return '입력한 연결 이유를 확인해 주세요. 저장하지 않았습니다.';
  return '객실 유형을 연결하지 못했습니다. 입력과 권한을 확인해 주세요.';
}

export function InitialRoomTypeBindingSection(props: Props) {
  const queryClient = useQueryClient();
  const [localOwnerState, setLocalOwnerState] = useState(emptyInitialRoomTypeBindingOwnerState);
  const setOwnerState = props.setOwnerState ?? setLocalOwnerState;
  const [roomId, setRoomId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [reason, setReason] = useState('');
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const focusTransition = useRef<{
    kind: 'review' | 'cancel' | 'success' | 'reason' | 'status' | 'review-control';
    scopeKey: string;
    source: Element | null;
  } | null>(null);
  const resultStatusRef = useRef<HTMLDivElement>(null);
  const messageStatusRef = useRef<HTMLParagraphElement>(null);
  const requestEpoch = props.ownerEpoch ?? 0;
  const selection = useRef({ roomId, typeId });
  selection.current = { roomId, typeId };
  const sessionSnapshot = props.runtime.session.getSnapshot();
  const scopeKey = JSON.stringify([
    props.runtime.apiNamespace,
    props.generation,
    props.propertyId.toLowerCase(),
    props.page.property.branch_id,
    props.authorization,
    sessionSnapshot.generation,
    sessionSnapshot.status,
    sessionSnapshot.deniedDomains,
    props.runtime.initialTypeBindingEnabled === true,
    props.isPropertyCurrent(),
    requestEpoch,
  ]);
  const scopeEpochRef = useRef({ key: scopeKey, value: 0 });
  if (scopeEpochRef.current.key !== scopeKey)
    scopeEpochRef.current = { key: scopeKey, value: scopeEpochRef.current.value + 1 };
  const scopeEpoch = scopeEpochRef.current.value;
  const previousScopeKey = useRef(scopeKey);
  const ownerState =
    props.ownerState ??
    (previousScopeKey.current === scopeKey
      ? localOwnerState
      : emptyInitialRoomTypeBindingOwnerState);
  useEffect(() => {
    if (previousScopeKey.current !== scopeKey) {
      setOwnerState(emptyInitialRoomTypeBindingOwnerState);
      previousScopeKey.current = scopeKey;
    }
  }, [scopeKey, setOwnerState]);

  const retained = ownerState.unknown ? ownerState.intent : null;
  if (
    retained &&
    !originalBindings.has(retained) &&
    retained.actorId &&
    retained.namespace === props.runtime.apiNamespace &&
    retained.generation === props.generation &&
    Object.isFrozen(retained.command)
  ) {
    originalBindings.set(retained, {
      command: retained.command,
      json: JSON.stringify(retained.command),
      actor: retained.actorId,
      namespace: retained.namespace,
      generation: retained.generation,
      propertyId: props.propertyId,
      roomId: retained.room.id,
      client: props.runtime.client,
    });
  }
  const saving = ownerState.pendingRequestId !== null;
  const { intent, unknown, message, receipt, freshState, freshDetails } = ownerState;
  const updateOwner = (partial: Partial<InitialRoomTypeBindingOwnerState>, requestId?: string) => {
    setOwnerState((current) =>
      requestId && current.pendingRequestId !== requestId ? current : { ...current, ...partial },
    );
  };
  const claimRequest = (kind: 'review' | 'write' | 'replay') => {
    if (props.claimOwnerRequest) {
      const requestId = crypto.randomUUID();
      return props.claimOwnerRequest(requestEpoch, requestId, kind) ? requestId : null;
    }
    if (ownerState.pendingRequestId) return null;
    const requestId = crypto.randomUUID();
    setOwnerState((current) =>
      current.pendingRequestId
        ? current
        : {
            ...current,
            pendingRequestId: requestId,
            pendingKind: kind,
          },
    );
    return requestId;
  };
  const isRequestCurrent = (requestId: string) =>
    props.isOwnerRequestCurrent ? props.isOwnerRequestCurrent(requestEpoch, requestId) : true;
  const finishRequest = (requestId: string) => {
    if (props.finishOwnerRequest) props.finishOwnerRequest(requestEpoch, requestId);
    else updateOwner({ pendingRequestId: null, pendingKind: null }, requestId);
  };

  const previousIntent = useRef(intent);
  const previousReceipt = useRef(receipt);
  const previousMessage = useRef(message);
  const scheduleFocus = (
    kind: NonNullable<typeof focusTransition.current>['kind'],
    source: Element | null = document.activeElement,
  ) => {
    focusTransition.current = {
      kind,
      scopeKey,
      source,
    };
  };
  useLayoutEffect(() => {
    const transition = focusTransition.current;
    const intentChanged = previousIntent.current !== intent;
    const receiptChanged = previousReceipt.current !== receipt;
    const messageChanged = previousMessage.current !== message;
    previousIntent.current = intent;
    previousReceipt.current = receipt;
    previousMessage.current = message;
    if (!transition) return;
    focusTransition.current = null;
    if (transition.scopeKey !== scopeKey) return;
    const changed =
      transition.kind === 'review' || transition.kind === 'cancel' || transition.kind === 'reason'
        ? intentChanged
        : transition.kind === 'success'
          ? receiptChanged
          : messageChanged || intentChanged;
    if (!changed) return;
    const active = document.activeElement;
    if (active !== transition.source && active !== document.body) return;
    if (transition.kind === 'review' && intent) reviewHeadingRef.current?.focus();
    else if (transition.kind === 'cancel' && !intent)
      (reasonRef.current ?? reviewButtonRef.current)?.focus();
    else if (transition.kind === 'success' && receipt) resultStatusRef.current?.focus();
    else if (transition.kind === 'reason' && !intent)
      (reasonRef.current ?? reviewButtonRef.current)?.focus();
    else if (transition.kind === 'status')
      (messageStatusRef.current ?? reviewButtonRef.current)?.focus();
    else if (transition.kind === 'review-control') reviewButtonRef.current?.focus();
  }, [intent, receipt, message, scopeKey]);

  const unboundRooms = props.page.rooms.filter((room) => room.coreRoomTypeId === null);
  const intentRoom = intent
    ? props.page.rooms.find((room) => room.id.toLowerCase() === intent.room.id.toLowerCase())
    : undefined;
  const intentRoomType = intentRoom?.coreRoomTypeId
    ? props.page.roomTypes.find(
        (type) => type.id.toLowerCase() === intentRoom.coreRoomTypeId?.toLowerCase(),
      )
    : undefined;
  if (
    props.page.initialBindingContract !== 'INITIAL_TYPE_BINDING_V1' ||
    (unboundRooms.length === 0 && !receipt && !(unknown && intent))
  )
    return null;

  const featureEnabled = props.runtime.initialTypeBindingEnabled === true;
  const hasRead = props.authorization !== null && canReadProperties(props.authorization);
  const hasWrite = props.authorization !== null && canWriteProperties(props.authorization);
  const selectedRoom = unboundRooms.find((room) => room.id === roomId) ?? unboundRooms[0];
  const selectedType =
    props.page.roomTypes.find((type) => type.id === typeId) ?? props.page.roomTypes[0];
  const canReview =
    featureEnabled &&
    !props.catalogError &&
    hasRead &&
    hasWrite &&
    Boolean(selectedRoom) &&
    Boolean(selectedType) &&
    reason.trim().length > 0 &&
    !saving &&
    intent === null;

  const isCurrent = () => {
    const current = props.runtime.session.getSnapshot();
    return (
      scopeEpochRef.current.value === scopeEpoch &&
      props.runtime.initialTypeBindingEnabled === true &&
      current.generation === props.generation &&
      current.status === 'authorized' &&
      current.authorization !== null &&
      JSON.stringify(current.authorization) === JSON.stringify(props.authorization) &&
      !current.deniedDomains.includes('property') &&
      canReadProperties(current.authorization) &&
      canWriteProperties(current.authorization) &&
      props.isPropertyCurrent()
    );
  };

  const refreshCatalog = async (
    requestId: string,
    cursors: PropertyCatalogCursors = props.cursors,
  ) => {
    const current = props.runtime.session.getSnapshot();
    if (
      current.generation !== props.generation ||
      current.status !== 'authorized' ||
      !props.isPropertyCurrent() ||
      !isCurrent() ||
      !isRequestCurrent(requestId)
    )
      throw new Error('현재 지점 또는 사용자 세션이 변경되었습니다.');
    try {
      return await readPropertyCatalog(
        props.runtime.client,
        props.propertyId,
        cursors,
        props.getOwnerRequestSignal?.(requestEpoch, requestId),
      );
    } catch (error) {
      if (isApiError(error) && error.status === 403 && isCurrent() && isRequestCurrent(requestId))
        props.runtime.session.reportDomainAccessDenied('property', props.generation);
      throw error;
    }
  };

  const review = async () => {
    if (!canReview || !selectedRoom || !selectedType || !isCurrent()) return;
    const requestId = claimRequest('review');
    if (!requestId) return;
    const source = document.activeElement;
    scheduleFocus('review', source);
    updateOwner(
      { message: null, receipt: null, freshState: null, freshDetails: null, receiptContext: null },
      requestId,
    );
    try {
      const fresh = await refreshCatalog(requestId);
      if (
        !isCurrent() ||
        !isRequestCurrent(requestId) ||
        (selection.current.roomId && selection.current.roomId !== selectedRoom.id) ||
        (selection.current.typeId && selection.current.typeId !== selectedType.id)
      )
        return;
      const observed = expectedFor(fresh, props.propertyId, selectedRoom.id, selectedType.id);
      if (!observed) {
        scheduleFocus('status', source);
        updateOwner(
          {
            message:
              '최신 객실·유형의 연결 정보가 달라졌습니다. 다시 조회하고 선택을 검토해 주세요.',
          },
          requestId,
        );
        return;
      }
      updateOwner(
        {
          intent: {
            room: observed.room,
            typeName: observed.typeName,
            snapshotKey: snapshotKey(observed.expected),
            originCursors: { ...props.cursors },
            propertyName: props.page.property.name,
            command: {
              command_id: crypto.randomUUID(),
              room_type_id: selectedType.id,
              expected: observed.expected,
              reason: reason.trim(),
            },
          },
        },
        requestId,
      );
    } catch (error) {
      if (!isCurrent() || !isRequestCurrent(requestId)) return;
      scheduleFocus('review-control', source);
      updateOwner({ message: failureMessage(error) }, requestId);
    } finally {
      finishRequest(requestId);
    }
  };

  const send = async (binding: BindingIntent, replay: boolean) => {
    if (!isCurrent() || saving) return;
    const source = document.activeElement;
    const requestId = claimRequest(replay ? 'replay' : 'write');
    if (!requestId) return;
    updateOwner({ message: null }, requestId);
    let attempted = false;
    try {
      if (!replay) {
        const fresh = await refreshCatalog(requestId);
        if (!isCurrent() || !isRequestCurrent(requestId)) return;
        const observed = expectedFor(
          fresh,
          props.propertyId,
          binding.room.id,
          binding.command.room_type_id,
        );
        if (!observed || snapshotKey(observed.expected) !== binding.snapshotKey) {
          scheduleFocus('status', source);
          updateOwner(
            {
              intent: null,
              unknown: false,
              message:
                '확인 뒤 객실 또는 유형 정보가 바뀌었습니다. 최신 정보를 다시 검토해 주세요.',
            },
            requestId,
          );
          await queryClient.invalidateQueries({
            queryKey: coreQueryKey(
              props.runtime.apiNamespace,
              props.generation,
              'property-catalog',
              props.propertyId,
            ),
          });
          return;
        }
      }
      const actor = await props.runtime.client.get<{ user_id: unknown }>('users/me');
      if (!isCurrent() || !isRequestCurrent(requestId) || !isUuid(actor.user_id)) return;
      let original = originalBindings.get(binding);
      if (replay) {
        if (ownerState.intent !== binding || !ownerState.unknown)
          throw new Error('원래 연결 요청 없음');
        if (
          actor.user_id !== original?.actor ||
          original.namespace !== props.runtime.apiNamespace ||
          original.generation !== props.generation ||
          original.client !== props.runtime.client ||
          original.propertyId !== props.propertyId ||
          original.roomId !== binding.room.id ||
          original.command !== binding.command ||
          original.json !== JSON.stringify(binding.command)
        )
          throw new Error('원래 연결 요청 또는 수행자가 변경되었습니다.');
      } else {
        validateInitialTypeBindingCommand(props.propertyId, binding.room.id, binding.command);
        binding.actorId = actor.user_id;
        binding.namespace = props.runtime.apiNamespace;
        binding.generation = props.generation;
        freezeRequest(binding.command);
        original = {
          command: binding.command,
          json: JSON.stringify(binding.command),
          actor: actor.user_id,
          namespace: props.runtime.apiNamespace,
          generation: props.generation,
          propertyId: props.propertyId,
          roomId: binding.room.id,
          client: props.runtime.client,
        };
        originalBindings.set(binding, original);
      }
      attempted = true;
      const result = replay
        ? parseInitialTypeBindingReceipt(
            await props.runtime.client.post<unknown>(
              `admin/properties/${original.propertyId}/rooms/${original.roomId}/initial-type-binding`,
              { json: original.command, expectedStatus: 200 },
            ),
            original.command,
            original.roomId,
          )
        : await bindInitialRoomType(
            props.runtime.client,
            props.propertyId,
            binding.room.id,
            binding.command,
          );
      if (!isCurrent() || !isRequestCurrent(requestId)) return;
      scheduleFocus('success', source);
      const previousContext = ownerState.receiptContext;
      const context =
        previousContext?.commandId === binding.command.command_id
          ? previousContext
          : {
              commandId: binding.command.command_id,
              roomId: binding.room.id,
              roomNo: binding.room.roomNo,
              typeName: binding.typeName,
              propertyName: binding.propertyName,
              cursors: binding.originCursors,
            };
      updateOwner(
        { intent: null, unknown: false, receipt: result, receiptContext: context },
        requestId,
      );
      await queryClient.invalidateQueries({
        queryKey: coreQueryKey(
          props.runtime.apiNamespace,
          props.generation,
          'property-catalog',
          props.propertyId,
        ),
      });
      try {
        const fresh = await refreshCatalog(requestId, context.cursors);
        if (!isCurrent() || !isRequestCurrent(requestId)) return;
        const currentRoom = fresh.rooms.find(
          (room) => room.id.toLowerCase() === context.roomId.toLowerCase(),
        );
        const currentType = fresh.roomTypes.find(
          (type) => type.id.toLowerCase() === currentRoom?.coreRoomTypeId?.toLowerCase(),
        );
        const currentRead = currentRoom
          ? currentRoom.coreRoomTypeId === null
            ? {
                freshState: null,
                freshDetails: `현재 조회: ${context.propertyName} · ${String(currentRoom.roomNo)}호가 목록에 있으며 현재 유형은 연결되지 않았습니다.`,
              }
            : currentType
              ? {
                  freshState: currentType.name,
                  freshDetails: `현재 조회: ${context.propertyName} · ${String(currentRoom.roomNo)}호 · ${currentType.name} · 현재 객실 revision ${String(currentRoom.version ?? '확인 불가')}`,
                }
              : {
                  freshState: null,
                  freshDetails: `현재 조회: ${context.propertyName} · ${String(currentRoom.roomNo)}호에 유형 ID ${currentRoom.coreRoomTypeId}가 있으나 현재 유형 목록에서 찾지 못했습니다.`,
                }
          : {
              freshState: null,
              freshDetails: `현재 조회: 원 객실 ${String(context.roomNo)}호가 목록에 없습니다.`,
            };
        updateOwner(currentRead, requestId);
      } catch {
        updateOwner(
          {
            freshState: null,
            freshDetails: '최초 연결 영수증은 확인했지만, 현재 객실 상태를 다시 읽지 못했습니다.',
          },
          requestId,
        );
      }
    } catch (error) {
      if (!isCurrent() || !isRequestCurrent(requestId)) return;
      if (replay) {
        updateOwner({ unknown: true, message: failureMessage(error) }, requestId);
        return;
      }
      if (isApiError(error) && error.status === 409) {
        scheduleFocus('reason', source);
        updateOwner(
          {
            intent: null,
            unknown: false,
            message:
              '객실·지점·유형 정보가 바뀌었습니다. 최신 정보를 다시 확인한 뒤 새로 검토해 주세요.',
          },
          requestId,
        );
        await queryClient.invalidateQueries({
          queryKey: coreQueryKey(
            props.runtime.apiNamespace,
            props.generation,
            'property-catalog',
            props.propertyId,
          ),
        });
      } else if (
        attempted &&
        isApiError(error) &&
        (error.kind === 'network' ||
          error.kind === 'timeout' ||
          (error.kind === 'invalid-response' &&
            error.status !== undefined &&
            error.status >= 200 &&
            error.status < 300) ||
          (error.kind === 'http' && (error.status ?? 0) >= 500))
      ) {
        scheduleFocus('status', source);
        updateOwner(
          {
            unknown: true,
            message:
              '저장 결과를 확인하지 못했습니다. 같은 명령과 같은 입력으로 결과를 다시 확인할 수 있습니다.',
          },
          requestId,
        );
      } else {
        scheduleFocus('status', source);
        updateOwner({ intent: null, unknown: false, message: failureMessage(error) }, requestId);
        if (isApiError(error) && error.status === 403)
          props.runtime.session.reportDomainAccessDenied('property', props.generation);
      }
    } finally {
      finishRequest(requestId);
    }
  };

  return (
    <section
      aria-label="객실 유형 최초 연결"
      className="border-border bg-muted/20 rounded-md border p-4"
    >
      <h3 className="title-sm">
        {unboundRooms.length > 0 ? '유형 연결이 필요한 객실' : '객실 유형 최초 연결 결과'}
      </h3>
      {unboundRooms.length > 0 && (
        <p className="body mt-1">
          {unboundRooms.length}개 객실에 현재 객실 유형이 연결되지 않았습니다. 연결할 지점 유형과
          사유를 확인해 주세요.
        </p>
      )}
      {!featureEnabled && (
        <p role="status" className="caption mt-2">
          현재 객실 유형 연결 기능을 사용할 수 없습니다.
        </p>
      )}
      {featureEnabled && !hasWrite && (
        <p role="status" className="caption mt-2">
          현재 계정은 객실 유형 연결 권한이 없습니다.
        </p>
      )}
      {props.catalogError && (
        <p role="status" className="caption mt-2">
          최신 객실 정보를 다시 읽지 못했습니다. 아래 명령 결과는 보존되어 있으며, 같은 입력으로
          서버의 최초 결과를 확인할 수 있습니다.
        </p>
      )}
      {unboundRooms.length > 0 && featureEnabled && hasRead && hasWrite && !props.catalogError && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="label">객실</span>
            <Select
              value={roomId.length > 0 ? roomId : (selectedRoom?.id ?? '')}
              disabled={Boolean(intent) || saving}
              items={unboundRooms.map((room) => ({
                value: room.id,
                label: `${String(room.roomNo)}호`,
              }))}
              onValueChange={(value) => {
                setRoomId(value ?? '');
              }}
            >
              <SelectTrigger aria-label="연결할 객실" className="w-full min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {unboundRooms.map((room) => (
                  <SelectItem key={room.id} value={room.id}>
                    {room.roomNo}호
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="label">연결할 객실 유형</span>
            <Select
              value={typeId.length > 0 ? typeId : (selectedType?.id ?? '')}
              disabled={Boolean(intent) || saving}
              items={props.page.roomTypes.map((type) => ({ value: type.id, label: type.name }))}
              onValueChange={(value) => {
                setTypeId(value ?? '');
              }}
            >
              <SelectTrigger aria-label="연결할 객실 유형" className="w-full min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {props.page.roomTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {intent === null && (
            <div className="sm:col-span-2">
              <label className="label" htmlFor="initial-room-type-reason">
                연결 사유
              </label>
              <Textarea
                id="initial-room-type-reason"
                ref={reasonRef}
                value={reason}
                maxLength={500}
                disabled={Boolean(intent) || saving}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
            </div>
          )}
          {intent === null && (
            <div className="sm:col-span-2">
              <Button ref={reviewButtonRef} disabled={!canReview} onClick={() => void review()}>
                {saving ? '최신 정보 확인 중' : '현재 정보 검토'}
              </Button>
            </div>
          )}
        </div>
      )}
      {intent && (
        <div
          className="border-border bg-background mt-4 rounded-md border p-3"
          role="group"
          aria-label="최초 연결 확인"
        >
          <h4 ref={reviewHeadingRef} tabIndex={-1} className="body-strong">
            {unknown ? '확인되지 않은 최초 연결 명령' : '연결 내용을 확인해 주세요'}
          </h4>
          <p className="body mt-1">
            {unknown
              ? `원 명령 대상: ${String(intent.room.roomNo)}호 → ${intent.typeName}`
              : `${String(intent.room.roomNo)}호 · 현재 유형 미연결 → ${intent.typeName}`}
          </p>
          <p className="caption mt-1">사유: {intent.command.reason}</p>
          {unknown && (
            <p className="caption mt-1">
              {intentRoom?.coreRoomTypeId
                ? `현재 catalog: ${String(intentRoom.roomNo)}호 · ${intentRoomType?.name ?? '연결된 유형 정보 확인 불가'}. `
                : ''}
              {unboundRooms.length > 0
                ? `다른 ${String(unboundRooms.length)}개 미연결 객실이 있습니다. 이전 명령 결과를 확인한 뒤 새 연결을 검토해 주세요.`
                : '현재 객실 정보와 관계없이 같은 명령의 최초 결과를 다시 확인합니다.'}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {unknown ? (
              <Button disabled={saving} onClick={() => void send(intent, true)}>
                {saving ? '같은 명령 확인 중' : '같은 명령으로 결과 확인'}
              </Button>
            ) : (
              <Button disabled={saving || !isCurrent()} onClick={() => void send(intent, false)}>
                {saving ? '연결 전 최신 정보 확인 중' : '이 유형으로 최초 연결'}
              </Button>
            )}
            {!unknown && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  scheduleFocus('cancel');
                  updateOwner({ intent: null, unknown: false, message: null });
                }}
              >
                취소
              </Button>
            )}
          </div>
        </div>
      )}
      {message && (
        <p
          ref={messageStatusRef}
          role="status"
          tabIndex={-1}
          className="body text-warning-foreground mt-3"
        >
          {message}
        </p>
      )}
      {receipt && (
        <div
          ref={resultStatusRef}
          className="border-border bg-background mt-3 rounded-md border p-3"
          role="status"
          tabIndex={-1}
        >
          <p className="body-strong">객실 유형 연결 완료</p>
          {ownerState.receiptContext && (
            <p className="body mt-1">
              {ownerState.receiptContext.propertyName} · {ownerState.receiptContext.roomNo}호 →{' '}
              {ownerState.receiptContext.typeName}
            </p>
          )}
          {freshState && <p className="body mt-1">현재 연결 유형: {freshState}</p>}
          <details className="mt-2">
            <summary className="caption cursor-pointer">결과 상세</summary>
            <p className="caption mt-1">
              최초 연결 결과: {receipt.effect} · 구조 revision {receipt.result_revision}
            </p>
            <p className="caption mt-1">
              명령 {receipt.command_id} · {receipt.received_at}
            </p>
            {freshDetails && <p className="caption mt-1">{freshDetails}</p>}
          </details>
        </div>
      )}
    </section>
  );
}
